import { CustomField } from "../store/ocrStore";

interface TextractRelationship {
  Type: string;
  Ids: string[];
}
interface TextractBoundingBox {
  Top: number;
  Left: number;
  Width: number;
  Height: number;
}

interface TextractGeometry {
  BoundingBox: TextractBoundingBox;
  Polygon: Array<{ X: number; Y: number }>;
}

interface TextractBlock {
  Id: string;
  BlockType: 'TABLE' | 'CELL' | 'LINE' | 'WORD' | 'PAGE' | 'KEY_VALUE_SET' | 'SELECTION_ELEMENT';
  Text?: string;
  Confidence?: number;
  Geometry?: TextractGeometry;
  Relationships?: TextractRelationship[];
  RowIndex?: number;
  ColumnIndex?: number;
  RowSpan?: number;
  ColumnSpan?: number;
  EntityTypes?: string[];
}

export interface CellData {
  id: string;
  text: string;
  rowSpan: number;
  colSpan: number;
  value: any;
  headers: {
    rowHeader: string;
    colHeader: string;
  };
}

export interface SpannedCell {
  spanned: true;
}

export interface TableData {
  id: string;
  annotationId: string;
  title: string;
  grid: (CellData | SpannedCell | null)[][];
}
interface AIGeneratedHeaders {
  cellId: string;
  headers: {
    row: string;
    col: string;
  };
}

interface PayloadItem {
  blockId: string;
  cellText: string | null;
  sensorTag?: string; 
  isCustom: boolean;
  aiContext?: {
    rowHeader: string;
    colHeader: string;
  };
  valueType?: 'Text' | 'Numeric' | 'Date';
}

const getText = (block: TextractBlock, fullMap: Map<string, TextractBlock>): string => {
  if (!block) return '';

  const childRel = block.Relationships?.find((r: TextractRelationship) => r.Type === 'CHILD');
  if (!childRel) return '';

  const childBlocks = childRel.Ids.map((id: string) => fullMap.get(id)).filter(
    (b): b is TextractBlock => Boolean(b)
  );

  const lineBlocks = childBlocks.filter((b: TextractBlock) => b.BlockType === 'LINE');

  if (!lineBlocks.length) {
    const wordBlocks = childBlocks.filter((b: TextractBlock) => b.BlockType === 'WORD');
    if (wordBlocks.length > 0) {
      wordBlocks.sort((a, b) => {
        const aLeft = a.Geometry?.BoundingBox.Left ?? 0;
        const bLeft = b.Geometry?.BoundingBox.Left ?? 0;
        return aLeft - bLeft;
      });
      return wordBlocks.map(w => w.Text || '').join(' ');
    }
    return '';
  }

  lineBlocks.sort((a, b) => {
    const aTop = a.Geometry?.BoundingBox.Top ?? 0;
    const bTop = b.Geometry?.BoundingBox.Top ?? 0;
    return aTop - bTop;
  });

  return lineBlocks.map(line => line.Text || '').join(' ');
};

export const parseTextractResponse = (
  textractBlocks: any[],
  aiHeaderMap: Map<string, { rowHeader: string; colHeader: string }> = new Map()
): Omit<TableData, 'id' | 'title' | 'annotationId'>[] => {
  if (!textractBlocks || textractBlocks.length === 0) return [];

  const blockMap = new Map(textractBlocks.map(b => [b.Id, b]));
  const tableBlocks = textractBlocks.filter(b => b.BlockType === 'TABLE');

  if (!tableBlocks.length) return [];

  return tableBlocks.map(tableBlock => {
    const childRel = tableBlock.Relationships?.find((r: any) => r.Type === 'CHILD');
    if (!childRel) {
      return { grid: [] };
    }
    const cellIds = childRel.Ids || [];
    if (!cellIds.length) {
      return { grid: [] };
    }

    const mergedBlocks = textractBlocks.filter(
      b =>
        b.BlockType === 'MERGED_CELL' &&
        b.Relationships?.find(r => r.Type === 'CHILD')?.Ids.some(id => cellIds.includes(id))
    );

    const mergedChildIds = new Set(
      mergedBlocks.flatMap(mb => mb.Relationships.find(r => r.Type === 'CHILD').Ids)
    );

    const syntheticCells = mergedBlocks.map(mb => {
      const mergedChildRel = mb.Relationships?.find(r => r.Type === 'CHILD');
      const mergedChildIdsList = mergedChildRel?.Ids || [];

      const mergedText = mergedChildIdsList
        .map(cid => {
          const childBlock = blockMap.get(cid);
          return childBlock ? getText(childBlock, blockMap) : '';
        })
        .filter(t => t.trim().length > 0)
        .join(' ');

      const cellText = mergedText || ' '; 

      return {
        id: mb.Id,
        rowIndex: mb.RowIndex,
        columnIndex: mb.ColumnIndex,
        rowSpan: mb.RowSpan || 1,
        colSpan: mb.ColumnSpan || 1,
        text: cellText,
        geometry: mb.Geometry,
      };
    });

    const rawCells = cellIds
      .map(id => blockMap.get(id))
      .filter(cb => cb && cb.BlockType === 'CELL' && !mergedChildIds.has(cb.Id))
      .map(cb => ({
        id: cb.Id,
        rowIndex: cb.RowIndex,
        columnIndex: cb.ColumnIndex,
        rowSpan: cb.RowSpan || 1,
        colSpan: cb.ColSpan || 1,
        text: getText(cb, blockMap) || ' ',
        geometry: cb.Geometry,
      }));

    const allCells = [...syntheticCells, ...rawCells];

    if (allCells.length === 0) {
      return { grid: [] };
    }

    const maxRow = Math.max(0, ...allCells.map((c: any) => c.rowIndex + c.rowSpan - 1));
    const maxCol = Math.max(0, ...allCells.map((c: any) => c.columnIndex + c.colSpan - 1));

    const grid = Array.from({ length: maxRow }, () => Array(maxCol).fill(null));

    allCells.forEach((cell: any) => {
      const r = cell.rowIndex - 1;
      const c = cell.columnIndex - 1;

      if (grid[r] && grid[r][c] === null) {
        const headers = aiHeaderMap.get(cell.id) || { rowHeader: '', colHeader: '' };

        grid[r][c] = {
          id: cell.id,
          value: cell.text,
          sensor: null,
          type: 'autocomplete',
          meta: { endpoint: '/ocr/getListOfDiSensors' },
          rowSpan: cell.rowSpan,
          colSpan: cell.colSpan,
          geometry: cell.geometry,
          headers: {
            rowHeader: headers.rowHeader,
            colHeader: headers.colHeader,
          },
          isHeader: isHeaderCell(cell.text, aiHeaderMap),
        };

        for (let dr = 0; dr < cell.rowSpan; dr++) {
          for (let dc = 0; dc < cell.colSpan; dc++) {
            if (dr !== 0 || dc !== 0) {
              if (grid[r + dr] && grid[r + dr][c + dc] !== undefined) {
                grid[r + dr][c + dc] = { spanned: true };
              }
            }
          }
        }
      }
    });

    return { grid };
  });
};

export const createSimplifiedCellsForVisionAI = (
  tableObjects: { grid: TableData['grid'] }[]
): { cellId: string; text: string | null }[] => {
  return tableObjects.flatMap(tableObject =>
    tableObject.grid
      .flat()
      .filter((cell): cell is CellData => !!(cell && !('spanned' in cell)))
      .map(cell => ({
        cellId: cell.id,
        text: cell.value,
      }))
  );
};

export const mergeAiHeadersIntoGrid = (
  grid: (CellData | SpannedCell | null)[][],
  aiHeaders: AIGeneratedHeaders[]
): (CellData | SpannedCell | null)[][] => {
  const headerMap = new Map<string, { row: string; col: string }>(
    aiHeaders.map(item => [item.cellId, item.headers])
  );
  return grid.map(row =>
    row.map(cell => {
      if (cell && !('spanned' in cell) && headerMap.has(cell.id)) {
        const headers = headerMap.get(cell.id);
        return {
          ...cell,
          headers: {
            rowHeader: headers?.row || '',
            colHeader: headers?.col || '',
          },
        };
      }
      return cell;
    })
  );
};

const isHeaderCell = (cellText, aiHeaderMap) => {
  if (!cellText || cellText.trim() === '' || cellText.trim() === 'Â ') {
    return false;
  }

  const cleanCellText = cellText.trim();

  for (const [, headers] of aiHeaderMap.entries()) {
    if (headers.rowHeader || headers.colHeader) {
      if (headers.rowHeader === cleanCellText || headers.colHeader === cleanCellText) {
        return true;
      }

      if (headers.rowHeader) {
        const rowParts = headers.rowHeader.split('>').map(part => part.trim());
        if (rowParts.includes(cleanCellText)) {
          return true;
        }
      }

      if (headers.colHeader) {
        const colParts = headers.colHeader.split('>').map(part => part.trim());
        if (colParts.includes(cleanCellText)) {
          return true;
        }
      }
    }
  }
  return false;
};

export const createSavePayload = (
  tables: Record<string, TableData>,
  customFields: CustomField[]
): PayloadItem[] => {
  const ocrPayload: PayloadItem[] = [];

  Object.values(tables).forEach(table => {
    table.grid.flat().forEach(cell => {
      if (cell && !('spanned' in cell) && !cell.isHeader && cell.sensor) {
        let sensorTag: string | undefined;

        if (typeof cell.sensor === 'string') {
          sensorTag = cell.sensor.trim();
        } else if (typeof cell.sensor === 'object' && cell.sensor?.sensor) {
          sensorTag = cell.sensor.sensorId.trim();
        }

        if (sensorTag) {
          ocrPayload.push({
            blockId: cell.id,
            cellText: cell.value,
            sensorTag: sensorTag,
            isCustom: false,
            aiContext: {
              rowHeader: cell.headers.rowHeader,
              colHeader: cell.headers.colHeader,
            },
          });
        }
      }
    });
  });

  const customPayload: PayloadItem[] = customFields.map(field => ({
    blockId: field.id,
    cellText: field.label,
    isCustom: true,
    valueType: field.dataType,
  }));

  return [...ocrPayload, ...customPayload];
};

export const isCellData = (cell: CellData | SpannedCell | null): cell is CellData => {
  return cell !== null && !('spanned' in cell);
};

export const isSpannedCell = (cell: CellData | SpannedCell | null): cell is SpannedCell => {
  return cell !== null && 'spanned' in cell;
};

export const getCellText = (cell: CellData | SpannedCell | null): string => {
  return isCellData(cell) ? cell.text : '';
};

export const getCellHeaders = (
  cell: CellData | SpannedCell | null
): { rowHeader: string; colHeader: string } => {
  return isCellData(cell) ? cell.headers : { rowHeader: '', colHeader: '' };
};
